import type { ApiError } from '@/types/auth';
import { getFieldLabel } from '@/lib/validation/fieldLabels';

export type UserMessageOptions = {
  fileSizeLimitMb?: number;
  allowedFileTypes?: string[];
};

export type UserFacingError = {
  title: string;
  message: string;
  status?: number;
  retryable: boolean;
  actionLabel?: string;
  fieldErrors?: Record<string, string>;
  suppressGlobalMessage: boolean;
  redirectToLogin: boolean;
};

type ErrorWithMetadata = Error & {
  status?: number;
  code?: string;
  body?: ApiError & { errors?: Record<string, string[] | string> };
  rawMessage?: string;
  userMessage?: UserFacingError;
  toastShown?: boolean;
};

function extractStatus(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const match = value.match(/\b(400|401|403|404|405|408|409|413|415|422|429|500|502|503|504)\b/);
    return match ? Number(match[1]) : undefined;
  }
  return undefined;
}

function normalizeFieldErrors(errors: unknown): Record<string, string> | undefined {
  if (!errors || typeof errors !== 'object') return undefined;

  const entries = Object.entries(errors as Record<string, unknown>)
    .map(([field, value]) => {
      if (Array.isArray(value)) {
        const first = value.find((item) => typeof item === 'string');
        return first ? [field, first] as const : undefined;
      }
      if (typeof value === 'string') return [field, value] as const;
      return undefined;
    })
    .filter((entry): entry is readonly [string, string] => Boolean(entry));

  if (!entries.length) return undefined;
  return Object.fromEntries(entries);
}

function inferStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return extractStatus(error);

  const candidate = error as {
    status?: unknown;
    message?: unknown;
    body?: { status?: unknown; message?: unknown };
    response?: { status?: unknown; data?: { status?: unknown } };
  };

  return (
    extractStatus(candidate.status) ??
    extractStatus(candidate.body?.status) ??
    extractStatus(candidate.response?.status) ??
    extractStatus(candidate.response?.data?.status) ??
    extractStatus(candidate.message)
  );
}

function inferMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return (error as ErrorWithMetadata).rawMessage ?? error.message;
  if (typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return 'Unexpected error';
}

/**
 * Extract the most specific human-readable error message from an API response body.
 * Falls back to status-code generic message if no specific detail is found.
 */
function extractBodyMessage(body: ApiError & { errors?: Record<string, string[] | string> } | undefined): string | undefined {
  if (!body) return undefined;
  // body.message or body.detail is the server-provided description
  const msg = typeof body.message === 'string' ? body.message.trim() : '';
  const detailValue = (body as ApiError & { detail?: unknown }).detail;
  const detail = typeof detailValue === 'string' ? detailValue.trim() : '';
  return msg || detail || undefined;
}

const CONTAINS_GUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function isTimeoutError(error: unknown, status?: number): boolean {
  if (status === 408) return true;
  const message = inferMessage(error).toLowerCase();
  const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code?: unknown }).code ?? '').toLowerCase() : '';
  const name = error instanceof Error ? error.name.toLowerCase() : '';

  // NP-611: a timeout can name itself rather than say "timeout" in its text —
  // api-client's own 30s cap aborts with a TimeoutError reading "Request to
  // <path> timed out after 30000ms", which matches none of the clauses above, so
  // that internal string was what reached the borrower.
  return message.includes('timeout') || code.includes('timeout') || name === 'aborterror' || name === 'timeouterror';
}

function isNetworkError(error: unknown, status?: number): boolean {
  if (status !== undefined) return false;
  const message = inferMessage(error).toLowerCase();
  const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code?: unknown }).code ?? '').toLowerCase() : '';

  return (
    error instanceof TypeError ||
    message.includes('failed to fetch') ||
    message.includes('network error') ||
    message.includes('load failed') ||
    message.includes('fetch') ||
    code === 'err_network'
  );
}

export function mapErrorToUserMessage(error: unknown, options: UserMessageOptions = {}): UserFacingError {
  // Log for debugging — but an EXPECTED response is not a console error.
  // A logged-out visitor hits 401/403 on the first authed call and a missing
  // resource returns 404; shouting those in red trains everyone to ignore the
  // console and buries real faults (console-noise cleanup 2026-07-29).
  // 5xx / network / unknown stay console.error; expected auth+404 statuses are
  // dev-only debug lines.
  const status = inferStatus(error);
  if (error !== null && error !== undefined) {
    const expected = status === 401 || status === 403 || status === 404;
    const detail = {
      error,
      status,
      message: inferMessage(error),
      body: typeof error === 'object' && 'body' in error ? (error as Record<string, unknown>).body : undefined,
    };
    if (expected) {
      if (import.meta.env.DEV) console.debug('[API]', detail);
    } else {
      console.error('[API Error]', detail);
    }
  }
  const body = typeof error === 'object' && error !== null && 'body' in error ? (error as { body?: ApiError & { errors?: Record<string, string[] | string> } }).body : undefined;
  const fieldErrors = normalizeFieldErrors(body?.errors);

  if (isTimeoutError(error, status)) {
    return {
      title: 'Request timed out',
      message: 'The request took too long. Please check your connection and try again.',
      status: 408,
      retryable: true,
      actionLabel: 'Try again',
      suppressGlobalMessage: false,
      redirectToLogin: false,
    };
  }

  if (isNetworkError(error, status)) {
    return {
      title: 'Connection issue',
      message: "Can't reach our servers right now. Please check your internet and try again.",
      retryable: true,
      actionLabel: 'Try again',
      suppressGlobalMessage: false,
      redirectToLogin: false,
    };
  }

  switch (status) {
    case 400: {
      // Build specific message listing field names when field errors are available
      let message400 = 'Some of the information looks off. Please review and try again.';
      if (fieldErrors) {
        const fieldNames = Object.keys(fieldErrors).map(getFieldLabel);
        if (fieldNames.length === 1) {
          message400 = `Please fix the error in ${fieldNames[0]}.`;
        } else if (fieldNames.length <= 5) {
          message400 = `Please fix errors in: ${fieldNames.join(', ')}.`;
        } else {
          message400 = `Please fix ${fieldNames.length} field errors. See highlighted fields below.`;
        }
      }
      return {
        title: 'Please review your information',
        message: message400,
        status,
        retryable: false,
        fieldErrors,
        suppressGlobalMessage: Boolean(fieldErrors),
        redirectToLogin: false,
      };
    }
    case 401:
      return {
        title: 'Session ended',
        message: 'Your session has ended. Please sign in again to continue.',
        status,
        retryable: false,
        suppressGlobalMessage: false,
        redirectToLogin: true,
      };
    case 403:
      return {
        title: 'Permission needed',
        message: "You don't have permission to do that.",
        status,
        retryable: false,
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    case 404: {
      // Determine context from error body to give a more specific message
      const bodyMsg404 = extractBodyMessage(body);
      // NP-676: the API's NotFoundException messages embed the id they looked up
      // ("Loan {guid} not found."). Now that parseErrorBody surfaces ProblemDetails
      // `detail`, echoing one verbatim would show a borrower an internal
      // identifier — so an id-bearing message feeds the wording below instead.
      const showsInternalId = !!bodyMsg404 && CONTAINS_GUID.test(bodyMsg404);
      const raw404 = `${bodyMsg404 ?? ''} ${inferMessage(error)}`.toLowerCase();
      let notFoundMsg = 'The requested resource could not be found. Please refresh the page or try again.';
      if (bodyMsg404 && !showsInternalId) {
        notFoundMsg = `${bodyMsg404}`;
      } else if (raw404.includes('loan') || raw404.includes('application')) {
        notFoundMsg = 'Your loan application was not found. Please try logging out and back in.';
      } else if (raw404.includes('borrower')) {
        notFoundMsg = 'Your borrower profile was not found. Please contact support.';
      } else if (raw404.includes('session') || raw404.includes('token') || raw404.includes('auth')) {
        notFoundMsg = 'Your session has expired. Please log in again.';
      } else if (raw404.includes('document')) {
        notFoundMsg = 'The document could not be found. It may have been deleted or moved.';
      } else if (raw404.includes('condition')) {
        notFoundMsg = 'The loan condition could not be found. Please refresh the page.';
      }
      return {
        title: 'Not found',
        message: notFoundMsg,
        status,
        retryable: false,
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    }
    case 405: {
      const bodyMsg405 = extractBodyMessage(body);
      return {
        title: 'Method not allowed',
        message: bodyMsg405
          ? `Request failed: ${bodyMsg405}`
          : 'This action is not allowed here. Please refresh and try again.',
        status,
        retryable: true,
        actionLabel: 'Try again',
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    }
    case 408:
      return {
        title: 'Request timed out',
        message: 'The request took too long. Please check your connection and try again.',
        status,
        retryable: true,
        actionLabel: 'Try again',
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    case 409: {
      // Vesta-direct wizard: 409 = Vesta-side failure (originator not found,
      // Vesta validation, outage). The server body is `{ error: "..." }` and
      // parseErrorBody surfaces it as the message — show it verbatim so the
      // user sees the actual reason. Not auto-retryable (spec §3.7): keep the
      // form dirty so the user can fix/retry the save explicitly.
      const bodyMsg409 = extractBodyMessage(body);
      return {
        title: bodyMsg409 ? "Couldn't save to the loan system" : 'Refresh needed',
        message: bodyMsg409 ?? 'It looks like this was already updated. Please refresh and try again.',
        status,
        retryable: false,
        actionLabel: 'Try again',
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    }
    case 413:
      return {
        title: 'File too large',
        message: `That file is too big. Please choose one under ${options.fileSizeLimitMb ?? 10} MB.`,
        status,
        retryable: false,
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    case 415:
      return {
        title: 'File type not supported',
        message: `We can't accept that file type. Please use ${(options.allowedFileTypes ?? ['PDF', 'PNG', 'JPG']).join(', ')}.`,
        status,
        retryable: false,
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    case 422:
      return {
        title: 'Please fix the highlighted fields',
        message: 'Some required information is missing or invalid.',
        status,
        retryable: false,
        fieldErrors,
        suppressGlobalMessage: Boolean(fieldErrors),
        redirectToLogin: false,
      };
    case 429:
      return {
        title: 'Rate limited',
        message: 'Too many attempts. Please wait a moment and try again.',
        status,
        retryable: true,
        actionLabel: 'Try again',
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    case 500:
    case 502:
    case 503:
    case 504: {
      const bodyMsg5xx = extractBodyMessage(body);
      const rawMsg = inferMessage(error);
      // Show the most specific detail available: body message > raw message > generic fallback
      let serverDetail = bodyMsg5xx ?? (rawMsg && rawMsg !== 'Unexpected error' ? rawMsg : undefined);
      // Strip noisy internal prefixes (e.g. "Error: Request failed with status code 500")
      if (serverDetail) {
        serverDetail = serverDetail
          .replace(/^Error:\s*/i, '')
          .replace(/^Request failed with status code \d+:\s*/i, '')
          .trim();
      }
      return {
        title: `Server error (${status})`,
        message: serverDetail
          ? `Server error: ${serverDetail}`
          : `Server error (HTTP ${status}). Our team has been notified — please try again in a moment.`,
        status,
        retryable: true,
        actionLabel: 'Try again',
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    }
    default: {
      const bodyMsgDefault = extractBodyMessage(body);
      const rawMsgDefault = inferMessage(error);
      let defaultDetail = bodyMsgDefault ?? (rawMsgDefault && rawMsgDefault !== 'Unexpected error' ? rawMsgDefault : undefined);
      if (defaultDetail) {
        defaultDetail = defaultDetail
          .replace(/^Error:\s*/i, '')
          .replace(/^Request failed with status code \d+:\s*/i, '')
          .trim();
      }
      // Part C fix: include the HTTP status in both title and message so developers
      // and users see a specific code rather than the generic "Request failed" prefix.
      // When the server provides a detailed message (via json.error / json.message), show it.
      // When it doesn't, give clear next-steps instead of a dead-end error string.
      return {
        title: status ? `Error (HTTP ${status})` : 'Unexpected error',
        message: defaultDetail
          ? defaultDetail  // Server supplied a clear message — show it directly
          : status
          ? `Something went wrong (HTTP ${status}). Please try again or contact support if this persists.`
          : 'An unexpected error occurred. Please try again or contact support if this persists.',
        status,
        retryable: true,
        actionLabel: 'Try again',
        suppressGlobalMessage: false,
        redirectToLogin: false,
      };
    }
  }
}

export function logRawError(error: unknown) {
  if (import.meta.env.DEV) {
    console.error('[user-visible-error]', error);
  }
}

export function withUserFriendlyError<T extends Error>(error: T, options: UserMessageOptions = {}): T & ErrorWithMetadata {
  const enhanced = error as T & ErrorWithMetadata;
  if (!enhanced.rawMessage) {
    enhanced.rawMessage = error.message;
  }
  enhanced.userMessage = mapErrorToUserMessage(enhanced, options);
  // NP-611: on a DOMException — the shape every aborted or timed-out fetch rejects
  // with — `message` is a prototype getter with no setter, so a plain assignment
  // throws "Cannot set property message of #<DOMException> which has only a getter"
  // and that TypeError goes on to replace the error it was meant to describe.
  // Defining an own data property shadows the getter, and leaves a normal Error
  // exactly as an assignment would.
  Object.defineProperty(enhanced, 'message', {
    value: enhanced.userMessage.message,
    writable: true,
    configurable: true,
  });
  return enhanced;
}
